import type { LocalDb, SqlValue } from "./local-db.js";
import type { SyncOp, SyncEntity } from "@coach/shared";

/**
 * Outbox — durable queue of writes that haven't been ack'd by the server.
 *
 * Rows in `sync_outbox` (see `schema.sql`) are atomic w.r.t. the local commit
 * that produced them: a repository UPDATE and the corresponding `enqueue()`
 * MUST run inside the same `localDb.transaction()` call so a crash never
 * leaves a local edit without a pending op.
 */

export interface OutboxRow {
  [column: string]: SqlValue;
  op_id: string;
  entity: string;
  kind: string;
  payload_json: string;
  updated_at: string;
  base_rev: number | null;
  attempts: number;
  created_at: string;
}

export async function enqueue(db: LocalDb, op: SyncOp): Promise<void> {
  await db.exec(
    `INSERT OR REPLACE INTO sync_outbox
      (op_id, entity, kind, payload_json, updated_at, base_rev, attempts)
     VALUES (?, ?, ?, ?, ?, ?, COALESCE((SELECT attempts FROM sync_outbox WHERE op_id = ?), 0))`,
    [
      op.opId,
      op.entity,
      op.kind,
      JSON.stringify(op.payload),
      op.updatedAt,
      op.baseRev as SqlValue,
      op.opId,
    ],
  );
}

export async function peek(db: LocalDb, limit = 200): Promise<SyncOp[]> {
  const rows = await db.query<OutboxRow>(
    `SELECT op_id, entity, kind, payload_json, updated_at, base_rev, attempts, created_at
       FROM sync_outbox ORDER BY created_at ASC LIMIT ?`,
    [limit],
  );
  return rows.map((r) => ({
    opId: r.op_id,
    entity: r.entity as SyncEntity,
    kind: r.kind as SyncOp["kind"],
    payload: JSON.parse(r.payload_json),
    updatedAt: r.updated_at,
    baseRev: r.base_rev,
  }));
}

export async function ack(db: LocalDb, opIds: string[]): Promise<void> {
  if (opIds.length === 0) return;
  const placeholders = opIds.map(() => "?").join(",");
  await db.exec(`DELETE FROM sync_outbox WHERE op_id IN (${placeholders})`, opIds);
}

export async function bumpAttempts(db: LocalDb, opIds: string[]): Promise<void> {
  if (opIds.length === 0) return;
  const placeholders = opIds.map(() => "?").join(",");
  await db.exec(
    `UPDATE sync_outbox SET attempts = attempts + 1 WHERE op_id IN (${placeholders})`,
    opIds,
  );
}

export async function pendingCount(db: LocalDb): Promise<number> {
  const rows = await db.query<{ n: number }>(`SELECT COUNT(*) AS n FROM sync_outbox`);
  return rows[0]?.n ?? 0;
}
